/**
 * Putting a brew's ingredients back on the shelves.
 *
 * ## Why this exists when ⌘Z already does it
 *
 * Deducting is one command batch, so one undo puts every gram back. That is the
 * right answer for the press that was a mistake, and the wrong answer for the
 * batch that was real for a fortnight and then got cancelled: by then the undo
 * stack has moved on, the tab has been closed, or the deduction happened on the
 * phone in the garage. `deduct-panel.tsx` used to say as much out loud — "while
 * it is still in this session's history" — which is a limitation, not a design.
 *
 * ## The snapshot is the record, not the recipe
 *
 * The plan is built from `costSnapshot.lines`, which froze *which lot* gave up
 * *how much* at deduct time. Re-planning from `recipeSnapshot` would be a
 * different and worse answer: the allocator picks oldest-lot-first against the
 * cupboard **as it is now**, so a return computed that way could push grain
 * back onto a sack that never gave any, and would silently disagree with the
 * deduction whenever a shortfall meant the batch got less than it asked for.
 *
 * A batch deducted before costing existed carries `inventoryDeductedAt` with no
 * snapshot. What left the shelves is genuinely unrecoverable there, so the plan
 * reports {@link InventoryReturnPlan.unrecorded} rather than guessing.
 *
 * ## A lot that no longer exists is rebuilt
 *
 * Deduct 5 lb off a sack, finish the sack, delete it, then cancel the batch:
 * the id in the snapshot points at nothing. Skipping the row would quietly
 * return less than was taken, so the lot is rebuilt from what the snapshot
 * kept — name, kind, quantity and the unit cost it carried — through
 * `inventory.restore`, the same command an undone delete uses. It comes back
 * holding exactly what this batch gave up and nothing else, because that is the
 * only quantity anyone can vouch for. It also comes back carrying the link the
 * cost line froze, so the rebuilt sack answers the availability check as the
 * deleted one did.
 *
 * ## A rebuilt lot wears one era's label, not half of each
 *
 * The one exception to "the snapshot is the record" is the *label* of a lot
 * whose link resolves, and it is not really an exception: a linked lot's name
 * and maker are owned by the ingredient, not by the lot
 * (`document/inventory.ts`, and `customIngredient.update` rewrites both on
 * every linked lot when the entry is renamed). So when the link resolves both
 * fields are read from the entry, and when it does not both come off the
 * snapshot. Never one from each: an ingredient renamed between the deduction
 * and the cancellation would otherwise put a sack on the shelf spelled with
 * last month's name and this month's maltster — a label that never existed,
 * and one the next rename would silently half-correct.
 *
 * The readout (`lines`) keeps the snapshot's name either way, because it is
 * answering a different question — what left the shelves, in the words it left
 * under — and a top-up row for a lot that still exists has no entry to read
 * from at all.
 */

import type { BatchDoc } from '../document/batch';
import { customIngredientMaker } from '../document/custom-ingredient';
import type { InventoryItemDoc, InventoryKind, InventoryUnit } from '../document/inventory';
import { buildInventoryItemDoc, inventoryUnitOf, roundQuantity } from '../document/inventory';
import type { CustomIngredientTable, InventoryTable } from './availability';
import type { InventoryAdjustment } from './deduct';

/** One lot going back, for the "here is what returns" readout. */
export interface ReturnLine {
  readonly inventoryItemId: string;
  readonly kind: InventoryKind;
  /** The name the lot carried when it was drawn down. */
  readonly name: string;
  readonly unit: InventoryUnit;
  /** How much goes back to this lot, **positive**. */
  readonly quantity: number;
  /** True when the lot is gone and this row rebuilds it rather than topping it up. */
  readonly recreated: boolean;
}

/** What {@link planInventoryReturn} produces. */
export interface InventoryReturnPlan {
  readonly batchId: string;
  /** When the deduction happened, or `null` when this batch was never deducted. */
  readonly deductedAt: number | null;
  /** Top-ups for lots that still exist. */
  readonly adjustments: readonly InventoryAdjustment[];
  /** Whole lots to put back, for ids the cupboard no longer holds. */
  readonly recreations: readonly InventoryItemDoc[];
  /** Both of the above, in snapshot order, for display. */
  readonly lines: readonly ReturnLine[];
  /** True when there is at least one movement to dispatch. */
  readonly returnable: boolean;
  /**
   * Deducted by a build that froze no cost snapshot, so what left is unknown.
   * The only honest answer is to say so and let the brewer restock by hand.
   */
  readonly unrecorded: boolean;
}

/** Options for {@link planInventoryReturn}. */
export interface ReturnOptions {
  /** What every movement records as its reason. Defaults to `Returned from "<batch name>"`. */
  readonly reason?: string;
  /** The clock any rebuilt lot is stamped with. Passed in; this module reads none. */
  readonly at: number;
  /**
   * The workspace's custom-ingredient table, so a rebuilt lot can wear the
   * name and maker its ingredient owns. Optional: without it the lot comes back
   * linked but labelled from the snapshot, and the next rename of the
   * ingredient brings both fields into line.
   */
  readonly customIngredients?: CustomIngredientTable | null;
}

/** The reason string a return records when the caller names none. */
export function defaultReturnReason(batch: BatchDoc): string {
  return `Returned from "${batch.name}"`.slice(0, 200);
}

const EMPTY = {
  adjustments: [],
  recreations: [],
  lines: [],
  returnable: false,
} as const;

/**
 * Plan putting one batch's deducted ingredients back.
 *
 * @param batch - The batch. Its `inventoryDeductedAt` is the guard and its
 *   `costSnapshot` is the record of what to return.
 * @param inventory - The workspace's `inventory` table, which decides per row
 *   whether the lot is topped up or rebuilt.
 */
export function planInventoryReturn(
  batch: BatchDoc,
  inventory: InventoryTable,
  options: ReturnOptions,
): InventoryReturnPlan {
  // Never deducted: there is nothing owed back, and the button should not exist.
  if (batch.inventoryDeductedAt === null) {
    return { batchId: batch.id, deductedAt: null, unrecorded: false, ...EMPTY };
  }
  if (batch.costSnapshot === null) {
    return { batchId: batch.id, deductedAt: batch.inventoryDeductedAt, unrecorded: true, ...EMPTY };
  }

  const reason = (options.reason ?? defaultReturnReason(batch)).slice(0, 200);
  const adjustments: InventoryAdjustment[] = [];
  const recreations: InventoryItemDoc[] = [];
  const lines: ReturnLine[] = [];

  for (const line of batch.costSnapshot.lines) {
    const quantity = roundQuantity(line.quantity);
    // A lot the deduction recorded but drew nothing from moves nothing back.
    if (quantity <= 0) continue;

    const held = inventory[line.inventoryItemId];
    if (held === undefined) {
      const ingredientId = line.ingredientId ?? null;
      const entry = ingredientId === null ? undefined : options.customIngredients?.[ingredientId];
      recreations.push(
        buildInventoryItemDoc({
          id: line.inventoryItemId,
          kind: line.kind,
          quantity,
          timestamp: options.at,
          unitCost: line.unitCost,
          // The link rides back with the lot (D-IL8). `inventory.restore` is a
          // verbatim upsert and fills nothing, so the label is assembled here:
          // both halves from the library when the link resolves, both halves
          // from the snapshot when it does not. See the module header on why
          // mixing the two eras is the one thing this must not do.
          ingredientId,
          name: entry === undefined ? line.name : entry.name,
          maker: entry === undefined ? null : customIngredientMaker(entry),
        }),
      );
    } else {
      adjustments.push({ itemId: line.inventoryItemId, delta: quantity, reason });
    }

    lines.push({
      inventoryItemId: line.inventoryItemId,
      kind: line.kind,
      name: line.name,
      unit: inventoryUnitOf(line.kind),
      quantity,
      recreated: held === undefined,
    });
  }

  return {
    batchId: batch.id,
    deductedAt: batch.inventoryDeductedAt,
    adjustments,
    recreations,
    lines,
    returnable: lines.length > 0,
    unrecorded: false,
  };
}
